/* ==========================================================================
   GEEKZ STORE - Scroll Reveal Animations
   IntersectionObserver with proper paint-frame timing.
   Tags sections with classes (not data-attributes) for CSS transitions.

   Sept 2026 fix: geekz-retro-fx.js switches every 3rd hidden section to the
   CRT variant (clip-path: inset(50% 0 50% 0)). A fully clipped element is
   not reported as intersecting, so those sections stayed invisible and left
   big empty gaps on the homepage (JDM LEGENDS / RETRO GAMING on phones).
   We now observe each section's unclipped .shopify-section wrapper, and a
   scroll fallback reveals anything on screen that the observer missed.

   Horizon 4.x: on desktop (990px and up) the page scrolls inside .page-wrapper
   instead of the window, so the fallback listens there as well.
   ========================================================================== */

(function () {
  // Respect user preference - bail entirely
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var PENDING = '.geekz-reveal:not(.geekz-visible), .geekz-stagger:not(.geekz-visible)';
  var observer = null;
  var targets = new Map(); // observed element -> elements it reveals
  var listening = false;
  var scrollTimer = null;

  function reveal(el) {
    if (!el) return;
    el.classList.add('geekz-visible');
    // Staggered grids inside a revealed section come in with it
    var grids = el.querySelectorAll('.geekz-stagger:not(.geekz-visible)');
    for (var i = 0; i < grids.length; i++) grids[i].classList.add('geekz-visible');
  }

  // Observe something that is never clipped: the section's .shopify-section
  // wrapper. Grids in sections that were already visible are observed directly.
  function targetFor(el) {
    var section = el.classList.contains('geekz-reveal') ? el : el.closest('.geekz-reveal');
    if (!section) return el;
    return section.closest('.shopify-section') || section.parentElement || section;
  }

  function createObserver() {
    if (observer) observer.disconnect();
    targets = new Map();
    if (!('IntersectionObserver' in window)) {
      observer = null;
      return;
    }
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var els = targets.get(entry.target) || [entry.target];
          els.forEach(reveal);
          targets.delete(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );
  }

  // Fallback: reveal anything on screen that is still hidden.
  // getBoundingClientRect ignores clip-path, so CRT sections are caught too.
  function revealInView() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var pending = document.querySelectorAll(PENDING);
    for (var i = 0; i < pending.length; i++) {
      var rect = pending[i].getBoundingClientRect();
      if (rect.top < vh - 40 && rect.bottom > 0) reveal(pending[i]);
    }
    if (listening && !document.querySelector(PENDING)) listen(false);
  }

  // Window, plus Horizon 4.x's desktop scroll container
  function listen(on) {
    var sources = [window];
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper) sources.push(wrapper);
    sources.forEach(function (source) {
      if (on) source.addEventListener('scroll', onScroll, { passive: true });
      else source.removeEventListener('scroll', onScroll);
    });
    if (on) window.addEventListener('resize', onScroll, { passive: true });
    else window.removeEventListener('resize', onScroll);
    listening = on;
  }

  function onScroll() {
    if (scrollTimer) return;
    scrollTimer = setTimeout(function () {
      scrollTimer = null;
      revealInView();
    }, 120);
  }

  function setup() {
    var viewportH = window.innerHeight;

    // --- Sections: reveal on scroll ---
    var sections = document.querySelectorAll(
      '#MainContent .section, .content-for-layout .section'
    );

    var belowFold = [];

    sections.forEach(function (el) {
      // Skip slideshow (above the fold hero)
      if (el.classList.contains('slideshow-section')) return;
      // Skip marquee (always visible, scrolling)
      if (el.closest('marquee-component')) return;

      var rect = el.getBoundingClientRect();

      // If section top is well below the current viewport, animate it
      if (rect.top > viewportH - 80) {
        belowFold.push(el);
      }
      // else: already visible, no animation needed
    });

    // --- Grids: stagger children ---
    var grids = document.querySelectorAll(
      '.section-resource-list__content, .section-carousel'
    );

    var belowFoldGrids = [];

    grids.forEach(function (grid) {
      var rect = grid.getBoundingClientRect();
      if (rect.top > viewportH - 80) {
        belowFoldGrids.push(grid);
      }
    });

    // Step 1: Add the "hidden" class - browser will paint opacity:0
    belowFold.forEach(function (el) { el.classList.add('geekz-reveal'); });
    belowFoldGrids.forEach(function (el) { el.classList.add('geekz-stagger'); });

    // Step 2: Wait two animation frames so the browser has painted the hidden state
    //         THEN start observing - so the transition from 0 to 1 is actually visible
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        createObserver();
        if (observer) {
          belowFold.concat(belowFoldGrids).forEach(function (el) {
            var target = targetFor(el);
            var list = targets.get(target);
            if (!list) {
              list = [];
              targets.set(target, list);
              observer.observe(target);
            }
            list.push(el);
          });
        }
        if (!listening) listen(true);
        revealInView();
      });
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // Theme editor: re-run when a section is added/reloaded
  document.addEventListener('shopify:section:load', function () {
    setup();
  });

  // Safety net: after retro-fx adds classes like geekz-crt-on,
  // reveal anything on screen that is still hidden.
  setTimeout(revealInView, 1200);
})();